import React, { useRef, useState } from 'react';
import { IconChevronDown } from '../icons';
import { SLOT_LABELS } from './queryOps';
import Popover from './Popover';
import { stopEvent } from './util';
import './search.css';

const SET_TWO_NOTE = 'Shows without named sets are left out.';

const JAM_OPTIONS = [
  { value: null, label: 'Any length', hint: '' },
  { value: 75, label: 'Top 25%', hint: 'Longer than 3 in 4 versions' },
  { value: 90, label: 'Top 10%', hint: 'Longer than 9 in 10 versions' },
];

// Slots offered for a band (B.2 features): none without `slots`; Set 2
// opener only with `setTwo` (bands with named sets).
export const slotChoices = (features) => {
  const f = features || {};
  if (!f.slots) return [];
  return ['any', 'opener', 'set2opener', 'setcloser', 'closer', 'encore'].filter((s) => s !== 'set2opener' || f.setTwo);
};

const Radio = ({ checked, label, hint, disabled, onSelect }) => (
  <button
    type="button"
    aria-pressed={!!checked}
    className={'ss-menu-item' + (checked ? ' checked' : '')}
    disabled={disabled}
    onClick={onSelect}
  >
    <span className="ss-radio" aria-hidden="true" />
    <span className="ss-menu-text">
      <span>{label}</span>
      {hint && <span className="ss-menu-hint">{hint}</span>}
    </span>
  </button>
);

// Caret menu for one song chip: where in the show (slots) and long versions
// (jam), each shown only when the band supports it.
//   onChange({ slot, jam }) with the full new pair.
// Optional (additive): `title` / `subtitle` header, `actions`
// [{ key, label, icon, onSelect, disabled }] listed under the choices,
// `slotFull` / `jamFull` when the query already holds the B.3 maximum,
// `buttonLabel` for the caret's accessible name, and `children` rendered
// inside the caret button (the chip name, so the whole chip opens the menu).
export default function SlotMenu({ slot, jam, features, onChange, title, subtitle, actions, slotFull, jamFull, buttonLabel, children }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const slots = slotChoices(features);
  const showJam = !!(features && features.jam);
  const cur = { slot: slot || 'any', jam: jam === 75 || jam === 90 ? jam : null };
  const acts = (actions || []).filter(Boolean);
  const pick = (patch) => {
    onChange && onChange({ ...cur, ...patch });
  };
  const run = (a) => {
    setOpen(false);
    a.onSelect && a.onSelect();
  };

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={'ss-caret' + (children ? ' wide' : '') + (open ? ' open' : '')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={buttonLabel || 'Song options'}
        title="Where in the show, length and more"
        onMouseDown={stopEvent}
        onTouchEnd={stopEvent}
        onKeyDown={stopEvent}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {children}
        <IconChevronDown size={12} />
      </button>
      <Popover anchorRef={anchor} open={open} onClose={() => setOpen(false)} label={title || 'Song options'}>
        {title && (
          <div className="ss-menu-head">
            <div className="ss-menu-title">{title}</div>
            {subtitle && <div className="ss-menu-hint">{subtitle}</div>}
          </div>
        )}
        {slots.length > 0 && (
          <div className="ss-menu-group" role="group" aria-label="Where in the show">
            <div className="ss-menu-label" aria-hidden="true">Where in the show</div>
            {slots.map((s) => (
              <Radio
                key={s}
                checked={cur.slot === s}
                label={SLOT_LABELS[s]}
                hint={s === 'set2opener' ? SET_TWO_NOTE : ''}
                disabled={s !== 'any' && cur.slot === 'any' && !!slotFull}
                onSelect={() => pick({ slot: s })}
              />
            ))}
            {!!(features && features.tracksFill) && <div className="ss-menu-note">From tape shows are left out of slot searches.</div>}
          </div>
        )}
        {showJam && (
          <div className="ss-menu-group" role="group" aria-label="Long version">
            <div className="ss-menu-label" aria-hidden="true">Long version</div>
            {JAM_OPTIONS.map((o) => (
              <Radio
                key={String(o.value)}
                checked={cur.jam === o.value}
                label={o.label}
                hint={o.hint}
                disabled={o.value != null && cur.jam == null && !!jamFull}
                onSelect={() => pick({ jam: o.value })}
              />
            ))}
          </div>
        )}
        {slotFull && cur.slot === 'any' && slots.length > 0 && <div className="ss-menu-note">Up to 6 songs can have a place in the show.</div>}
        {jamFull && cur.jam == null && showJam && <div className="ss-menu-note">Up to 4 songs can ask for a long version.</div>}
        {acts.length > 0 && (
          <div className="ss-menu-group ss-menu-actions">
            {acts.map((a) => (
              <button key={a.key} type="button" className="ss-menu-item" disabled={!!a.disabled} onClick={() => run(a)}>
                <span className="ss-menu-icon" aria-hidden="true">
                  {a.icon}
                </span>
                <span className="ss-menu-text">
                  <span>{a.label}</span>
                  {a.hint && <span className="ss-menu-hint">{a.hint}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </Popover>
    </>
  );
}
